import { API, usePlaybooks } from "./playbookData";
import React, { useState } from "react";
import { ElfsightWidget } from "./SiteLayout";
import { CalendlyLink, SmartLink } from "./SiteHeader";
import COPY from "./content/siteCopy.json";
import { Testimonials, useReviewsLabel } from "./Testimonials";

// Shared page sections. Default copy = homepage (source/latest/index.html, verbatim). City pages pass overrides.
import { WingedPercent } from "./RatesChrome";
// Copy lives in content/siteCopy.json — shared with the backend prerender (backend/fixtures/site_copy.json mirror).
export const EMPLOYERS = COPY.employers;

export const HOME_INTENTS = COPY.help.intents;

const TOOLS = COPY.tools.items;

const STEPS = COPY.how.steps;

export const HOME_FAQS = COPY.home.faqs;

export const Anchor = ({ href, className, children, ...r }) => href.startsWith("#")
  ? <a href={href} className={className} {...r}>{children}</a>
  : <SmartLink href={href} className={className} {...r}>{children}</SmartLink>;

export const Intro = ({ eyebrow, heading, text, id }) => (
  <div className="intro"><div><div className="eyebrow">{eyebrow}</div><h2 className="display" id={id}>{heading}</h2></div>{text && <p>{text}</p>}</div>
);

const WIN_CONFETTI = Array.from({ length: 28 }, (_, i) => {
  const a = (i / 28) * 360 + (i % 3) * 5;
  return { a, d: 120 + (i * 37) % 130, r: (i * 53) % 360, s: 7 + (i % 4) * 2, c: ["#ff3356", "#ff6c82", "#14283b", "#ffd166", "#e8f4f5"][i % 5], t: 1.5 + (i % 5) * 0.2 };
});

// Finish-line moment: ribbon is taut on load, "Winning" sprints in from the left, snaps the tape, confetti.
// Phases: armed (word hidden, ribbon up) → run (animations) → done. Plays once per browser session; skipped for reduced-motion or if the hero is off-screen.
const WIN_KEY = "hm_win_seen";
export function WinningWord({ word, storageKey = WIN_KEY }) {
  const [phase, setPhase] = React.useState(() => {
    if (typeof window === "undefined" || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return "done";
    if (window.sessionStorage.getItem(storageKey)) return "done";
    return "armed";
  });
  const [burst, setBurst] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (phase !== "armed") return undefined;
    const t1 = setTimeout(() => {
      const r = ref.current?.getBoundingClientRect();
      if (!r || r.bottom < 0 || r.top > window.innerHeight) { setPhase("done"); return; }
      window.sessionStorage.setItem(storageKey, "1");
      setPhase("run");
    }, 400);
    return () => clearTimeout(t1);
  }, [phase]);
  React.useEffect(() => {
    if (phase !== "run") return undefined;
    const t2 = setTimeout(() => setBurst(true), 520);
    const t3 = setTimeout(() => setPhase("done"), 2600);
    return () => { clearTimeout(t2); clearTimeout(t3); };
  }, [phase]);
  return (
    <span className={`hero-win is-${phase}`} ref={ref} data-testid="hero-winning-word" data-phase={phase}>
      <span className="hero-win-word">{word}</span>
      <svg className="hero-win-stroke" viewBox="0 0 520 70" preserveAspectRatio="none" aria-hidden="true">
        <filter id="hero-win-rough" x="-5%" y="-40%" width="110%" height="180%">
          <feTurbulence type="fractalNoise" baseFrequency=".045 .12" numOctaves="3" seed="7" result="n" />
          <feDisplacementMap in="SourceGraphic" in2="n" scale="7" xChannelSelector="R" yChannelSelector="G" />
        </filter>
        <g filter="url(#hero-win-rough)">
          <path d="M6 44 C 120 24, 300 10, 512 26 L 514 42 C 330 34, 150 48, 10 60 Z" />
          <path d="M20 54 C 140 46, 320 40, 500 42 L 498 46 C 330 47, 150 54, 24 60 Z" opacity=".75" />
          <path d="M60 60 L 120 56 L 118 58 L 62 63 Z M 380 47 L 440 46 L 439 48 L 382 50 Z" opacity=".6" />
        </g>
      </svg>
      {phase !== "done" && <span className="hero-ribbon" aria-hidden="true" data-testid="hero-winning-ribbon"><i className="l" /><i className="r" /></span>}
      {burst && phase !== "done" && <span className="hiw-confetti hero-confetti" aria-hidden="true" data-testid="hero-winning-confetti">{WIN_CONFETTI.map((c, i) => <i key={i} style={{ "--a": `${c.a}deg`, "--d": `${c.d}px`, "--r": `${c.r}deg`, "--s": `${c.s}px`, "--t": `${c.t}s`, background: c.c }} />)}</span>}
    </span>
  );
}

// SEO: the eyebrow carries the keyword-rich <h1>; the big display line is an <h2> styled exactly like the old h1.
function HeroTitle({ h1 }) {
  const m = h1.match(/^(.*?)(Winning)(.*)$/);
  if (!m) return <h2 className="hero-title">{h1}</h2>;
  return <h2 className="hero-title">{m[1]}<WinningWord word={m[2]} />{m[3]}</h2>;
}

export function Hero({ eyebrow, h1 = COPY.hero.title, text, testId = "home-hero" }) {
  const mascot = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("hero") === "mascot";
  const reviews = useReviewsLabel();
  return (
    <section className="hero" data-testid={testId}>
      <div className="hero-grid wrap">
        <div>
          <h1 className="eyebrow hero-eyebrow" data-testid="hero-h1">{eyebrow}</h1>
          <HeroTitle h1={h1} />
          <p>{text}</p>
          <div className="actions"><CalendlyLink className="btn" testId="hero-cta">{COPY.hero.cta}</CalendlyLink><a className="textlink" href="#help">{COPY.hero.secondary}</a></div>
          <div className="micro">{COPY.hero.micro}</div>
        </div>
        {mascot ? <HeroFlock /> : <aside className="proof-card" aria-label="Hello Mortgage highlights">
          <h3>{COPY.hero.proof_heading}</h3>
          {COPY.hero.proof.map(([k, v]) => <div className="stat" key={k}><strong>{k.replace("{reviews}", reviews)}</strong><span>{v}</span></div>)}
        </aside>}
      </div>
    </section>
  );
}

// Preview-only alternative hero visual (?hero=mascot): the winged-% flock with a live-rate chip.
function HeroFlock() {
  const [rates, setRates] = React.useState(null);
  React.useEffect(() => { fetch(`${process.env.REACT_APP_BACKEND_URL}/api/public/featured-rates`).then((r) => r.json()).then(setRates).catch(() => {}); }, []);
  const c = (rates?.cards || []).find((x) => /5yr fixed/i.test(x.key || x.label || ""));
  return (
    <div className="hero-flock" aria-hidden="true">
      <div className="rt-flock">{[["a", 1], ["b", .5], ["c", .36]].map(([k, sc]) => <WingedPercent key={k} variant={k} scale={sc} />)}</div>
      <a className="hero-flock-chip" href="/rates/"><span className="live-dot" /><b>{c?.rate != null ? `${Number(c.rate).toFixed(2)}%` : "Today's rates"}</b><span>{c?.rate != null ? "5-yr fixed · live" : "live from lender desks"}</span><em>See all rates →</em></a>
    </div>
  );
}

export const Statement = () => <div className="statement">{COPY.statement[0]} <em>{COPY.statement[1]}</em></div>;

const EMPLOYER_CITY_TAGS = { edmonton: "Edmonton", calgary: "Calgary" };
const ALWAYS_SHOW_EMPLOYERS = ["Athabasca University"];
export function employersFor(region) {
  if (!region) return EMPLOYERS;
  return EMPLOYERS.filter((n) => ALWAYS_SHOW_EMPLOYERS.includes(n) || !Object.entries(EMPLOYER_CITY_TAGS).some(([r, tag]) => r !== region && n.includes(tag)));
}

// region: anchor-city key (edmonton/calgary) → that city's employer list and "All over Edmonton."
// cityName: a satellite city (Camrose, Airdrie…) → province-wide employers, "All over Alberta.", "Camrose homebuyers".
export function EmployerProof({ region = null, cityName = null }) {
  const tag = EMPLOYER_CITY_TAGS[region] || null;
  const list = tag || !cityName ? employersFor(tag ? region : null) : EMPLOYERS.filter((n) => !Object.values(EMPLOYER_CITY_TAGS).some((t) => n.includes(t)));
  const who = tag ? COPY.employer_proof.who_city.replace("{where}", tag) : cityName ? COPY.employer_proof.who_city.replace("{where}", cityName) : COPY.employer_proof.who_default;
  return (
    <section className="employer-proof" aria-labelledby="employer-heading" data-testid="employer-proof" data-region={region || "all"}>
      <h2 id="employer-heading">{COPY.employer_proof.heading} <em>{tag ? COPY.employer_proof.heading_em.replace("{where}", tag) : COPY.employer_proof.heading_em_province}</em></h2>
      <p className="employer-lead">{COPY.employer_proof.lead.replace("{who}", who)}</p>
      <div className="employer-marquee" aria-label="Organizations where Hello Mortgage clients work">
        <div className="employer-track">
          {[false, true].map((dup) => (
            <div className="employer-group" aria-hidden={dup || undefined} key={String(dup)}>
              {list.map((n) => <React.Fragment key={n}><span className="employer-name">{n}</span><span className="employer-dot" aria-hidden="true">•</span></React.Fragment>)}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HelpSection({ eyebrow = COPY.help.eyebrow, text = COPY.help.text, intents = HOME_INTENTS }) {
  return (
    <section id="help"><div className="wrap">
      <Intro eyebrow={eyebrow} heading={COPY.help.heading} text={text} />
      <div className="intent-grid">{intents.map(([num, h, p, href]) => <Anchor className="intent" href={href} key={num}><span className="num">{num}</span><h3>{h}</h3><p>{p}</p><span className="arrow">→</span></Anchor>)}</div>
    </div></section>
  );
}

export function ToolsBand({ eyebrow = COPY.tools.eyebrow, text = COPY.tools.text }) {
  return (
    <section className="tools" id="tools"><div className="wrap">
      <Intro eyebrow={eyebrow} heading={COPY.tools.heading} text={text} />
      <div className="tool-grid">{TOOLS.map(([tag, h, p, fine, link, href]) => <SmartLink className="tool" href={href} key={tag}><span className="tag">{tag}</span><h3>{h}</h3><p>{p}</p><div className="fine">{fine}</div><div className="tool-link">{link}</div></SmartLink>)}</div>
      <PlaybookStrip />
    </div></section>
  );
}

export function PlaybookStrip() {
  const list = usePlaybooks();
  if (!list?.length) return null;
  return (
    <div className="pb-strip" data-testid="playbook-strip">
      <div className="pb-strip-head"><span className="tag">{COPY.tools.playbooks_tag}</span><h3>{COPY.tools.playbooks_heading}</h3><SmartLink className="textlink" href="/playbooks/" style={{ color: "#fff", borderColor: "#ff6c82" }}>See all playbooks</SmartLink></div>
      <div className="pb-strip-row">{list.map((p) => <SmartLink className="pb-mini" href={`/playbooks/${p.slug}/`} key={p.slug} data-testid={`playbook-mini-${p.slug}`}>{p.cover_url && <img src={`${API}${p.cover_url}`} alt={`${p.label} — free Hello Mortgage playbook cover`} loading="lazy" />}<span>{p.label}</span></SmartLink>)}</div>
    </div>
  );
}

export function HowItWorks({ eyebrow = COPY.how.eyebrow, text = COPY.how.text }) {
  return (
    <section className="how" id="how-it-works"><div className="wrap">
      <Intro eyebrow={eyebrow} heading={COPY.how.heading} text={text} />
      <div className="steps">{STEPS.map(([n, h, em, img, p]) => <article className="step" data-step={n} key={n}><img className="step-art" src={img} alt={`Step ${n}: ${h} ${em}`.replace(/\s+/g, " ").trim()} loading="lazy" width="210" height="210" data-testid={`how-step-art-${n}`} /><h3>{h}<em>{em}</em></h3><p>{p}</p></article>)}</div>
    </div></section>
  );
}

export function StrategyBand({ text = COPY.strategy.text, heading = COPY.strategy.heading }) {
  return (
    <section className="strategy" id="difference">
      <div className="strategy-grid">
        <div className="strategy-copy"><div className="eyebrow" style={{ color: "#fff" }}>{COPY.strategy.eyebrow}</div><h2 className="display">{heading}</h2><p>{text}</p><a className="btn light" href="#talk">{COPY.strategy.cta}</a></div>
        <div className="comparison"><h3>{COPY.strategy.compare_heading}</h3>{COPY.strategy.compare.map(([k, v]) => <div className="compare-row" key={k}><strong>{k}</strong><span>{v}</span></div>)}</div>
      </div>
    </section>
  );
}

export function ReviewsSection({ topics = [], city = "", region = [] }) {
  return <Testimonials topics={topics} city={city} region={region} />;
}

export function TeamSection({ paragraphs = COPY.team.paragraphs }) {
  return (
    <section className="team" id="team"><div className="wrap team-panel">
      <div><div className="eyebrow">{COPY.team.eyebrow}</div><h2 className="display">{COPY.team.heading}</h2>{paragraphs.map((p) => <p key={p}>{p}</p>)}<SmartLink className="textlink" href="/why-hello/#team">{COPY.team.link}</SmartLink></div>
      <div className="team-badges">{COPY.team.badges.map(([k, v]) => <div className="badge" key={k}><span>{k}</span>{v}</div>)}</div>
    </div></section>
  );
}

export function MathSection({ text = COPY.math.text, fine = COPY.math.fine, cta = COPY.math.cta, ratesHref = "/rates" }) {
  const RatesLink = /^https?:\/\//.test(ratesHref) ? "a" : SmartLink;
  const ratesProps = RatesLink === "a" ? { href: ratesHref } : { href: ratesHref };
  return (
    <section className="math"><div className="wrap">
      <Intro eyebrow={COPY.math.eyebrow} heading={COPY.math.heading} text={text} />
      <div className="math-grid">
        <div className="math-side"><h3>{COPY.math.crunch_heading}</h3><p>{COPY.math.crunch_text}</p><div className="mini-grid">{COPY.math.minis.map(([n, sub, href]) => <SmartLink className="mini" href={href} key={href}>{n}<small>{sub}</small></SmartLink>)}</div></div>
        <div className="math-side"><h3>{COPY.math.fine_heading}</h3><p>{fine}</p><p><strong>{COPY.math.fine_strong}</strong> {COPY.math.fine_tail}</p><div className="actions" style={{ marginTop: 28 }}><RatesLink className="btn" {...ratesProps} data-testid="view-current-rates">{cta}</RatesLink><a className="textlink" href="#difference">{COPY.math.secondary}</a></div></div>
      </div>
    </div></section>
  );
}

export function Faq({ items, visible }) {
  const [open, setOpen] = useState(0);
  const [all, setAll] = useState(!visible || items.length <= visible);
  const shown = all ? items : items.slice(0, visible);
  return (
    <div className="faq-list">
      {shown.map(([q, a], i) => (
        <div className={`faq-item${open === i ? " open" : ""}`} key={q}>
          <button className="faq-q" type="button" aria-expanded={open === i} aria-controls={`faq-a-${i}`} id={`faq-q-${i}`} onClick={() => setOpen(open === i ? null : i)} data-testid={`faq-q-${i}`}>{q}<span className="plus" aria-hidden="true">+</span></button>
          <div className="faq-a" id={`faq-a-${i}`} role="region" aria-labelledby={`faq-q-${i}`}>{a}</div>
        </div>
      ))}
      {!all && <button type="button" className="textlink faq-more" onClick={() => setAll(true)} data-testid="faq-more">More questions ({items.length - visible}) →</button>}
    </div>
  );
}

// H2 carries the local keyword ("Your Camrose mortgage questions live here."); the old display heading
// ("The honest answers.") becomes a small label sitting directly above the questions.
export const FaqSection = ({ items, eyebrow = COPY.faq.eyebrow, heading = COPY.faq.heading, label = COPY.faq.label, visible }) => (
  <section className="faq" data-testid="faq-section"><div className="wrap"><div className="intro"><div><div className="eyebrow">{eyebrow}</div><h2 className="display" data-testid="faq-heading">{heading}</h2></div><div className="faq-col"><div className="faq-label" data-testid="faq-label">{label}</div><Faq items={items} visible={visible} /></div></div></div></section>
);

export const FinalCta = ({ text = COPY.final.text }) => (
  <section className="final" id="talk"><div className="wrap"><p className="display final-title">{COPY.final.title}</p><p>{text}</p><CalendlyLink className="btn" testId="final-cta">{COPY.final.cta}</CalendlyLink></div></section>
);
